export type HeroHighlight = {
  icon?: string
  title?: string
}

export type HeroCta = {
  label?: string
  icon?: string
  icon_position?: 'left' | 'right'
  appearance?: 'button' | 'link'
  type?: 'internal' | 'external' | 'scroll_to'
  post_id?: number
  url?: string
  external_url?: string
  scroll_target_uuid?: string
}

export type HeroVideo = {
  source?: 'local' | 'external'
  file_id?: number
  file_url?: string
  url?: string
  embed_url?: string
  provider_id?: string
}

export type HeroMedia = {
  type?: 'none' | 'image' | 'video' | 'illustration'
  has_media?: boolean
  has_shout_box?: boolean
  image_id?: number
  image_url?: string
  image_alt?: string
  illustration?: string
  illustration_label?: string
  shout_box?: HeroShoutBox
  video?: HeroVideo
}

export type HeroShoutBox = {
  show?: boolean
  title?: string
  type?: string
  top?: string
  content?: string
  bottom?: string
}

export type RawHero = {
  icon?: string
  supertitle?: string
  title?: string
  content?: string
  content_html?: string
  disclaimer?: string
  highlight?: HeroHighlight | HeroHighlight[] | null
  highlights?: HeroHighlight[] | null
  cta?: HeroCta | HeroCta[] | null
  ctas?: HeroCta[] | null
  media?: HeroMedia | null
  shoutbox?: HeroShoutBox | null
  shout_box?: HeroShoutBox | null
  layout?: string
  height?: string
  size?: string
  width?: string
}

export type Hero = {
  icon?: string
  supertitle?: string
  title?: string
  content?: string
  content_html?: string
  disclaimer?: string
  highlights: HeroHighlight[]
  ctas: HeroCta[]
  media?: HeroMedia
  shout_box?: HeroShoutBox
  layout?: string
  height?: string
  size?: string
  width?: string
}

function cleanString(value: unknown) {
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  return trimmed || undefined
}

function normalizeMediaType(value: unknown): HeroMedia['type'] {
  return value === 'none' || value === 'image' || value === 'video' || value === 'illustration'
    ? value
    : undefined
}

function normalizeVideoSource(value: unknown): HeroVideo['source'] {
  return value === 'local' || value === 'external'
    ? value
    : undefined
}

function normalizeList<T>(input: T | T[] | null | undefined) {
  if (!input) return []
  return Array.isArray(input) ? input : [input]
}

function normalizeHighlights(input: RawHero['highlight'] | RawHero['highlights']) {
  return normalizeList(input)
    .map((item) => ({
      icon: cleanString(item?.icon),
      title: cleanString(item?.title),
    }))
    .filter((item) => item.icon || item.title)
}

function normalizeCtas(input: RawHero['cta'] | RawHero['ctas']) {
  return normalizeList(input)
    .map((item) => ({
      label: cleanString(item?.label),
      icon: cleanString(item?.icon),
      icon_position:
        item?.icon_position === 'left' || item?.icon_position === 'right'
          ? item.icon_position
          : undefined,
      appearance:
        item?.appearance === 'button' || item?.appearance === 'link'
          ? item.appearance
          : undefined,
      type:
        item?.type === 'internal' || item?.type === 'external' || item?.type === 'scroll_to'
          ? item.type
          : undefined,
      post_id: typeof item?.post_id === 'number' ? item.post_id : undefined,
      url: cleanString(item?.url),
      external_url: cleanString(item?.external_url),
      scroll_target_uuid: cleanString(item?.scroll_target_uuid),
    }))
    .filter((item) => item.label && item.type)
}

function normalizeShoutBox(input: HeroShoutBox | null | undefined) {
  if (!input || typeof input !== 'object') return undefined

  const shoutBox: HeroShoutBox = {
    show: input.show === true,
    title: cleanString(input.title),
    type: cleanString(input.type),
    top: cleanString(input.top),
    content: cleanString(input.content),
    bottom: cleanString(input.bottom),
  }

  return shoutBox.show || shoutBox.title || shoutBox.type || shoutBox.top || shoutBox.content || shoutBox.bottom
    ? shoutBox
    : undefined
}

function normalizeMedia(input: RawHero['media']) {
  if (!input || typeof input !== 'object') return undefined

  const media: HeroMedia = {
    type: normalizeMediaType(input.type),
    has_media: Boolean(input.has_media),
    has_shout_box: input.has_shout_box === true,
    image_id: typeof input.image_id === 'number' ? input.image_id : undefined,
    image_url: cleanString(input.image_url),
    image_alt: cleanString(input.image_alt),
    illustration: cleanString(input.illustration),
    illustration_label: cleanString(input.illustration_label),
    shout_box: normalizeShoutBox(input.shout_box),
    video: input.video
      ? {
          source: normalizeVideoSource(input.video.source),
          file_id: typeof input.video.file_id === 'number' ? input.video.file_id : undefined,
          file_url: cleanString(input.video.file_url),
          url: cleanString(input.video.url),
          embed_url: cleanString(input.video.embed_url),
          provider_id: cleanString(input.video.provider_id),
        }
      : undefined,
  }

  const hasUsefulMedia =
    media.has_media ||
    media.type === 'image' ||
    media.type === 'video' ||
    media.type === 'illustration' ||
    !!media.image_url ||
    !!media.illustration ||
    !!media.illustration_label ||
    media.has_shout_box ||
    !!media.shout_box ||
    !!media.video?.file_url ||
    !!media.video?.url ||
    !!media.video?.embed_url

  return hasUsefulMedia ? media : undefined
}

export function normalizeHero(input: RawHero | null | undefined) {
  if (!input || typeof input !== 'object') return undefined

  const normalized: Hero = {
    icon: cleanString(input.icon),
    supertitle: cleanString(input.supertitle),
    title: cleanString(input.title),
    content: cleanString(input.content),
    content_html: cleanString(input.content_html),
    disclaimer: cleanString(input.disclaimer),
    highlights: normalizeHighlights(input.highlights ?? input.highlight),
    ctas: normalizeCtas(input.ctas ?? input.cta),
    media: normalizeMedia(input.media),
    shout_box: normalizeShoutBox(input.shout_box ?? input.shoutbox),
    layout: cleanString(input.layout),
    height: cleanString(input.height),
    size: cleanString(input.size),
    width: cleanString(input.width),
  }

  const hasContent =
    !!normalized.icon ||
    !!normalized.supertitle ||
    !!normalized.title ||
    !!normalized.content ||
    !!normalized.content_html ||
    !!normalized.disclaimer ||
    normalized.highlights.length > 0 ||
    normalized.ctas.length > 0 ||
    !!normalized.media ||
    !!normalized.shout_box ||
    !!normalized.layout ||
    !!normalized.height ||
    !!normalized.size ||
    !!normalized.width

  return hasContent ? normalized : undefined
}
